import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Star, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SelfLoveRitualGuide() {
  const navigate = useNavigate();

  const ritualIdeas = [
    { ritual: "Morning Mirror Affirmations", time: "2 min", how: "Look in your eyes and say 3 kind things about yourself" },
    { ritual: "Gratitude for Your Body", time: "3 min", how: "Thank specific body parts for what they do for you" },
    { ritual: "Solo Date", time: "1-2 hours", how: "Take yourself somewhere you love—café, museum, park" },
    { ritual: "Self-Care Evening", time: "1 hour", how: "Bath, skincare, candles, music—treat yourself like royalty" },
    { ritual: "Journal Love Letter", time: "15 min", how: "Write a letter to yourself from your future self who loves you" },
    { ritual: "Celebration Moments", time: "1 min", how: "Pause to acknowledge every small win throughout the day" }
  ];

  const bodyGratitude = [
    { part: "Hands", gratitude: "For all the work they do, the people they've touched" },
    { part: "Legs", gratitude: "For carrying you through life, every step of your journey" },
    { part: "Eyes", gratitude: "For showing you sunsets, loved ones' faces, beauty" },
    { part: "Heart", gratitude: "For beating without you asking, for loving so deeply" },
    { part: "Stomach", gratitude: "For digesting, for butterflies when excited, for intuition" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">
            Week 7 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Self-Love Ritual
          </h1>
          <p className="text-xl text-secondary-600">Celebrate the Wonderful You</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Heart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Celebration</h3>
              <p className="text-rose-100 text-lg">
                Look in the mirror and speak kindly about your body for 3 minutes. Thank your legs for walking, 
                your arms for hugging, your heart for loving. Create a self-love ritual you'll do weekly.
              </p>
            </div>
          </div>
        </section>

        {/* Body Gratitude */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-rose-500" />
            Body Gratitude Practice
          </h2>
          <div className="space-y-3">
            {bodyGratitude.map((item, index) => (
              <div key={index} className="p-4 bg-rose-50 rounded-xl">
                <p className="font-bold text-rose-700 mb-1">Thank your {item.part}...</p>
                <p className="text-secondary-600">{item.gratitude}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Ritual Ideas */}
        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Create Your Self-Love Ritual</h2>
          <div className="space-y-4">
            {ritualIdeas.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <h3 className="font-bold text-rose-700 md:w-1/3">{item.ritual}</h3>
                <span className="text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded-full w-fit">{item.time}</span>
                <p className="text-sm text-secondary-600 md:flex-1">{item.how}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SelfLoveRitualGuide;
